import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';

const textareaCls = 'w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

const errMsg =(err, fallback) => err?.response?.data?.detail || fallback;

/**
 * Admin payment settings that sit beside the gateway list: marketplace commission (university / provider payouts),
 * Stripe Connect readiness check, and the editable prices of one-time services.
 */
export default function PaymentSettingsPanel() {
  const { api } = useAuth();
  const [commission, setCommission] = useState('');
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState('');

  const load = useCallback(async () => {
    try { setCommission(String((await api.get('/admin/settings/marketplace-commission')).data.percent)); } catch { /* ignore */ }
    try { setServices((await api.get('/admin/service-catalog')).data); } catch { /* ignore */ }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const saveCommission = async () => {
    setSaving('commission');
    try {
      await api.put('/admin/settings/marketplace-commission', { percent: parseFloat(commission) });
      toast.success('Commission saved');
    } catch (err) { toast.error(errMsg(err, 'Failed to save commission')); }
    finally { setSaving(''); }
  };

  const runCheck = async () => {
    setChecking(true);
    try { setCheck((await api.get('/admin/payments/connect-check')).data); }
    catch (err) { toast.error(errMsg(err, 'Check failed')); }
    finally { setChecking(false); }
  };

  const patchService = (type, field, value) =>
    setServices(list => list.map(s => (s.service_type === type ? { ...s, [field]: value } : s)));

  const saveService = async (s) => {
    setSaving(s.service_type);
    try {
      await api.put(`/admin/service-catalog/${s.service_type}`, {
        name: s.name,
        description: s.description || '',
        duration: s.duration || '',
        badge: s.badge || '',
        price: parseFloat(s.price),
        is_active: s.is_active !== false,
        features: (s.features || []).map(f => f.trim()).filter(Boolean),
      });
      toast.success(`${s.name} updated`);
      load();
    } catch (err) { toast.error(errMsg(err, 'Failed to save service')); }
    finally { setSaving(''); }
  };

  const Flag = ({ ok, label }) => (
    <span className="inline-flex items-center gap-1 text-sm">
      {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-red-500" />}{label}
    </span>
  );

  return (
    <div className="space-y-6" data-testid="payment-settings-panel">
      <Card>
        <CardHeader>
          <CardTitle>Marketplace commission &amp; payouts</CardTitle>
          <CardDescription>
            Percentage WorksRun keeps from course, event and university sales; the rest is transferred to the seller's Stripe
            Connect account. Per-seller overrides are set on the user in the Users tab.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end gap-3 max-w-sm">
            <div className="flex-1 space-y-1">
              <Label>Platform commission (%)</Label>
              <Input type="number" min={0} max={100} step="0.5" value={commission} onChange={e => setCommission(e.target.value)} data-testid="commission-input" />
            </div>
            <Button onClick={saveCommission} disabled={saving === 'commission'} className="bg-[#003366] hover:bg-[#003366]/90">
              {saving === 'commission' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </div>

          <div className="border-t pt-4 space-y-2">
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={runCheck} disabled={checking} data-testid="connect-check-btn">
                {checking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Check Stripe Connect readiness
              </Button>
              {check && <Badge className={check.test_mode ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>{check.test_mode ? 'Test keys' : 'Live keys'}</Badge>}
            </div>
            {check && (
              <div className="space-y-1" data-testid="connect-check-result">
                <div className="flex flex-wrap gap-4">
                  <Flag ok={check.stripe_key_configured} label="Secret key" />
                  <Flag ok={check.connect_enabled} label="Connect enabled" />
                  <Flag ok={check.webhook_secret_configured} label="Webhook secret" />
                </div>
                {check.message && <p className="text-xs text-muted-foreground">{check.message}</p>}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Universities and providers complete onboarding themselves from their own dashboard (“Connect Payouts”); admins cannot
              enter another party's bank or identity details.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>One-time services</CardTitle>
          <CardDescription>
            Edit the name, price (excl. VAT), description, badge and “Includes” list shown on the Premium Services page.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {services.map(s => (
            <div key={s.service_type} className="border rounded-lg p-3 space-y-3" data-testid={`service-row-${s.service_type}`}>
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[200px] space-y-1">
                  <Label className="text-xs">Name <span className="text-muted-foreground">({s.service_type})</span></Label>
                  <Input value={s.name || ''} onChange={e => patchService(s.service_type, 'name', e.target.value)}
                    data-testid={`service-name-${s.service_type}`} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Price excl. VAT (£)</Label>
                  <Input type="number" min={0} step="0.01" className="w-32" value={s.price ?? 0}
                    onChange={e => patchService(s.service_type, 'price', e.target.value)} data-testid={`service-price-${s.service_type}`} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Badge (optional)</Label>
                  <Input className="w-36" value={s.badge || ''} placeholder="e.g. Popular"
                    onChange={e => patchService(s.service_type, 'badge', e.target.value)} />
                </div>
                <div className="flex items-center gap-2 pb-2">
                  <Switch checked={s.is_active !== false} onCheckedChange={v => patchService(s.service_type, 'is_active', v)} />
                  <span className="text-xs">Active</span>
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-xs">Description</Label>
                  <textarea rows={3} className={textareaCls} value={s.description || ''}
                    onChange={e => patchService(s.service_type, 'description', e.target.value)} />
                  <Label className="text-xs">Duration</Label>
                  <Input value={s.duration || ''} onChange={e => patchService(s.service_type, 'duration', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Includes (one item per line)</Label>
                  <textarea rows={7} className={textareaCls} value={(s.features || []).join('\n')}
                    data-testid={`service-features-${s.service_type}`}
                    onChange={e => patchService(s.service_type, 'features', e.target.value.split('\n'))} />
                </div>
              </div>
              <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={() => saveService(s)} disabled={saving === s.service_type}>
                  {saving === s.service_type ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                </Button>
              </div>
            </div>
          ))}
          {services.length === 0 && <p className="text-sm text-muted-foreground">No services found.</p>}
        </CardContent>
      </Card>
    </div>
  );
}
